import {
  borderWidth,
  color,
  radius,
  size,
  spacing,
  StyleVars,
  TRANSPARENT,
  TypographyName,
} from "./types";

/* ------------------------------------------------------------------ Button */

export type ButtonSize = "small" | "medium" | "large";
export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "tertiary-v2"
  | "white"
  | "accent"
  | "ghost"
  | "danger";

export const buttonSpec = {
  defaults: { size: "medium" as ButtonSize, variant: "primary" as ButtonVariant },
  sizes: {
    small: {
      vars: {
        "--button-height": size("control-sm"),
        "--button-padding-x": spacing("3"),
        "--button-gap": spacing("1-5"),
        "--button-radius": radius("md"),
        "--button-icon-size": size("icon-sm"),
      } satisfies StyleVars,
      typography: "caption-12-semibold" as TypographyName,
    },
    medium: {
      vars: {
        "--button-height": size("control-md"),
        "--button-padding-x": spacing("4"),
        "--button-gap": spacing("2"),
        "--button-radius": radius("lg"),
        "--button-icon-size": size("icon-md"),
      } satisfies StyleVars,
      typography: "text-14-medium" as TypographyName,
    },
    large: {
      vars: {
        "--button-height": size("control-lg"),
        "--button-padding-x": spacing("5"),
        "--button-gap": spacing("2"),
        "--button-radius": radius("lg"),
        "--button-icon-size": size("icon-md"),
      } satisfies StyleVars,
      typography: "text-14-medium" as TypographyName,
    },
  },
  variants: {
    primary: {
      "--button-bg": color("interaction-black"),
      "--button-bg-hover": color("interaction-black-hv"),
      "--button-fg": color("text-inverse"),
      "--button-border": TRANSPARENT,
    },
    secondary: {
      "--button-bg": color("interaction-white"),
      "--button-bg-hover": color("interaction-violet-light-hv"),
      "--button-fg": color("interaction-violet"),
      "--button-border": color("interaction-violet-light"),
    },
    tertiary: {
      "--button-bg": color("interaction-grey-light"),
      "--button-bg-hover": color("interaction-grey-light-hv"),
      "--button-fg": color("text-primary"),
      "--button-border": TRANSPARENT,
    },
    "tertiary-v2": {
      "--button-bg": color("background-interaction-tertiary"),
      "--button-bg-hover": color("background-interaction-tertiary-hv"),
      "--button-fg": color("text-primary"),
      "--button-border": TRANSPARENT,
    },
    white: {
      "--button-bg": color("interaction-white"),
      "--button-bg-hover": color("interaction-white-hv"),
      "--button-fg": color("text-primary"),
      "--button-border": color("border-secondary"),
    },
    accent: {
      "--button-bg": color("interaction-violet"),
      "--button-bg-hover": color("interaction-violet-hv"),
      "--button-fg": color("text-on-accent"),
      "--button-border": TRANSPARENT,
    },
    ghost: {
      // Третьестепенное действие: без заливки, чтобы не спорило с основными.
      "--button-bg": TRANSPARENT,
      "--button-bg-hover": TRANSPARENT,
      "--button-fg": color("text-secondary-v2"),
      "--button-fg-hover": color("text-primary"),
      "--button-border": TRANSPARENT,
    },
    danger: {
      "--button-bg": color("status-error"),
      "--button-bg-hover": color("status-error"),
      "--button-fg": color("status-accent-error"),
      "--button-border": TRANSPARENT,
    },
  } satisfies Record<ButtonVariant, StyleVars>,
  base: {
    "--button-border-width": borderWidth("hairline"),
    "--button-disabled-opacity": "0.45",
  } satisfies StyleVars,
};

/* ------------------------------------------------------------------- Field */

export type FieldSize = "small" | "medium" | "large";

/** Общая геометрия для input / select / search / textarea. */
export const fieldSpec = {
  defaults: { size: "medium" as FieldSize },
  sizes: {
    small: {
      vars: {
        "--field-height": size("control-sm"),
        "--field-padding-x": spacing("3"),
        "--field-radius": radius("lg"),
        "--field-gap": spacing("2"),
      } satisfies StyleVars,
      typography: "caption-13-medium" as TypographyName,
    },
    medium: {
      vars: {
        "--field-height": size("control-md"),
        "--field-padding-x": spacing("4"),
        "--field-radius": radius("lg"),
        "--field-gap": spacing("2"),
      } satisfies StyleVars,
      typography: "text-14-medium" as TypographyName,
    },
    large: {
      vars: {
        "--field-height": size("control-lg"),
        "--field-padding-x": spacing("4"),
        "--field-radius": radius("lg"),
        "--field-gap": spacing("2"),
      } satisfies StyleVars,
      typography: "text-14-medium" as TypographyName,
    },
  },
  base: {
    "--field-bg": color("background-gray-light"),
    "--field-bg-focus": color("background-white"),
    "--field-fg": color("text-primary"),
    "--field-placeholder": color("text-tertiary"),
    "--field-border": TRANSPARENT,
    "--field-border-focus": color("interaction-violet"),
    "--field-border-error": color("status-accent-error"),
    "--field-border-width": borderWidth("s"),
    "--field-shadow-focus": "var(--shadow-focus-violet)",
    "--field-label-color": color("text-secondary-v2"),
    "--field-hint-color": color("text-secondary-v2"),
    "--field-error-color": color("status-accent-error"),
    "--field-label-gap": spacing("2"),
    "--field-icon-size": size("icon-sm"),
    "--field-disabled-opacity": "0.5",
    "--field-textarea-min-height": "160px",
    // Системный крестик у input[type=search] мелкий и синий — рисуем свой.
    "--field-clear-size": size("control-sm"),
    "--field-clear-icon": size("icon-sm"),
    "--field-clear-fg": color("text-secondary-v2"),
    "--field-clear-fg-hover": color("text-primary"),
    "--field-clear-bg-hover": color("interaction-grey-light-hv"),
  } satisfies StyleVars,
  labelTypography: "caption-12-medium" as TypographyName,
  hintTypography: "caption-11-medium" as TypographyName,
};

/* ---------------------------------------------------------------- Dropdown */

export const dropdownSpec = {
  vars: {
    "--dropdown-bg": color("background-white"),
    "--dropdown-border": color("border-secondary"),
    "--dropdown-radius": radius("xl"),
    "--dropdown-padding": spacing("3"),
    "--dropdown-gap": spacing("2"),
    "--dropdown-shadow": "var(--shadow-float)",
    "--dropdown-item-height": "34px",
    "--dropdown-item-padding-x": spacing("3"),
    "--dropdown-item-radius": radius("md"),
    "--dropdown-item-bg-hover": color("interaction-grey-light"),
    "--dropdown-item-bg-selected": color("background-violet-10"),
    "--dropdown-item-fg-selected": color("interaction-violet"),
    "--dropdown-max-height": "260px",
  } satisfies StyleVars,
  itemTypography: "text-14-medium" as TypographyName,
};

/* ------------------------------------------------------- Switch / Checkbox */

export const switchSpec = {
  vars: {
    "--switch-width": "36px",
    "--switch-height": "20px",
    "--switch-knob": size("icon-sm"),
    "--switch-padding": "2px",
    "--switch-bg-off": color("background-interaction-tertiary"),
    "--switch-bg-on": color("interaction-violet"),
    "--switch-knob-bg": color("interaction-white"),
    "--switch-radius": radius("full"),
  } satisfies StyleVars,
  labelTypography: "text-14-medium" as TypographyName,
};

export const checkboxSpec = {
  vars: {
    "--checkbox-size": size("icon-md"),
    "--checkbox-radius": radius("md"),
    "--checkbox-border": color("border-tertiary"),
    "--checkbox-border-width": borderWidth("s"),
    "--checkbox-bg": color("background-white"),
    "--checkbox-bg-checked": color("interaction-violet"),
    "--checkbox-fg-checked": color("text-on-accent"),
    "--checkbox-gap": spacing("2"),
  } satisfies StyleVars,
  labelTypography: "text-14-medium" as TypographyName,
};

export const radioSpec = {
  vars: {
    "--radio-size": size("icon-md"),
    "--radio-border": color("border-tertiary"),
    "--radio-border-width": borderWidth("s"),
    "--radio-bg-checked": color("interaction-violet"),
    "--radio-dot": spacing("2"),
    "--radio-gap": spacing("2"),
  } satisfies StyleVars,
  labelTypography: "text-14-medium" as TypographyName,
};

export const sliderSpec = {
  vars: {
    "--slider-track-height": spacing("1"),
    "--slider-track-bg": color("background-interaction-tertiary"),
    "--slider-track-fill": color("interaction-violet"),
    "--slider-thumb-size": size("icon-sm"),
    "--slider-thumb-bg": color("interaction-white"),
    "--slider-thumb-border": color("interaction-violet"),
    "--slider-thumb-border-width": borderWidth("sm"),
    "--slider-radius": radius("full"),
  } satisfies StyleVars,
};
